import { useEffect, useState } from "react";
import { api } from "../lib/api";
import TextCardGrid from "../components/TextCardGrid";
import { Link, useNavigate } from "react-router-dom";
import { Shuffle } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "../components/Seo";

const TYPE_CARDS = [
  { key: "Pensamientos", to: "/prosa/pensamientos", label: "Pensamientos", main: "Prosa" },
  { key: "Relatos", to: "/prosa/relatos", label: "Relatos", main: "Prosa" },
  { key: "Microrelatos", to: "/prosa/microrelatos", label: "Microrelatos", main: "Prosa" },
  { key: "Poemas", to: "/versos/poemas", label: "Poemas", main: "Versos" },
];

const SECTIONS = [
  { sub: "Pensamientos", to: "/prosa/pensamientos", label: "Pensamientos", overline: "Prosa" },
  { sub: "Relatos", to: "/prosa/relatos", label: "Relatos", overline: "Prosa" },
  { sub: "Microrelatos", to: "/prosa/microrelatos", label: "Microrelatos", overline: "Prosa" },
  { sub: "Poemas", to: "/versos/poemas", label: "Poemas", overline: "Versos" },
];

export default function Home() {
  const [bySub, setBySub] = useState({});
  const [counts, setCounts] = useState(null);
  const [trending, setTrending] = useState(null);
  const [audioCount, setAudioCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [randomLoading, setRandomLoading] = useState(false);
  const navigate = useNavigate();

  const openRandom = async () => {
    setRandomLoading(true);
    try {
      const { data } = await api.get("/texts/random");
      navigate(`/texto/${data.id}`);
    } catch {
      toast.error("Aún no hay textos para sortear.");
    } finally {
      setRandomLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [c, tr, ...subResponses] = await Promise.all([
          api.get("/categories/counts"),
          api.get("/texts/trending"),
          ...SECTIONS.map((s) =>
            api.get("/texts", { params: { subcategory: s.sub, limit: 6 } })
          ),
        ]);
        setCounts(c.data);
        setTrending(tr.data);
        try {
          const a = await api.get("/audios");
          setAudioCount(Array.isArray(a.data) ? a.data.length : 0);
        } catch {
          setAudioCount(0);
        }
        const grouped = {};
        SECTIONS.forEach((s, i) => {
          grouped[s.sub] = subResponses[i].data;
        });
        setBySub(grouped);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const totalAll = counts
    ? (counts.Prosa?.total || 0) + (counts.Versos?.total || 0)
    : 0;

  const countFor = (sub) => {
    if (!counts) return 0;
    if (sub === "Poemas") return counts.Versos?.Poemas || 0;
    return counts.Prosa?.[sub] || 0;
  };

  const homeOrigin =
    typeof window !== "undefined" ? window.location.origin : "https://rincon.javiloboautor.com";

  return (
    <div data-testid="home-page">
      <Seo
        description="El rincón de mis palabras — bitácora literaria de Javilobo. Un espacio reposado para pensamientos, relatos, microrelatos y poemas, con recitales en voz del autor."
        image={`${homeOrigin}/og-default.jpg`}
        url={homeOrigin + "/"}
        type="website"
      />
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 pt-16 md:pt-28 pb-16 md:pb-24">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 fade-in-up">
            <div
              className="text-xs md:text-sm uppercase tracking-[0.35em] mb-8"
              style={{ color: "var(--rincon-secondary)" }}
            >
              Bitácora literaria · Javilobo
            </div>
            <h1
              className="font-display font-light tracking-tight leading-[0.95] text-5xl sm:text-6xl md:text-7xl"
              style={{ color: "var(--rincon-text)" }}
            >
              El rincón de <em className="italic">mis palabras</em>.
            </h1>
            <p
              className="font-body italic mt-10 max-w-xl text-xl md:text-2xl leading-relaxed"
              style={{ color: "var(--rincon-text-muted)" }}
            >
              Un espacio reposado para pensamientos, relatos, microrelatos y
              poemas. Textos nacidos del oficio lento de observar, callar y,
              sólo entonces, escribir.
            </p>
            <div className="mt-12 flex flex-wrap items-center gap-8">
              <Link
                to="/prosa"
                data-testid="hero-prosa-link"
                className="font-display text-xl ink-link"
                style={{ color: "var(--rincon-primary)" }}
              >
                → Entrar en la Prosa
              </Link>
              <Link
                to="/versos"
                data-testid="hero-versos-link"
                className="font-display text-xl ink-link"
                style={{ color: "var(--rincon-primary)" }}
              >
                → Leer los Versos
              </Link>
              <Link
                to="/autor"
                data-testid="hero-autor-link"
                className="font-display text-xl ink-link"
                style={{ color: "var(--rincon-primary)" }}
              >
                → Sobre el autor
              </Link>
            </div>
          </div>
          <div className="hidden md:block md:col-span-5">
            <div
              className="aspect-[4/5] bg-cover bg-center fade-in-up"
              style={{
                backgroundImage:
                  "url('https://images.unsplash.com/photo-1531087131490-07836ca4341d?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80')",
                filter: "sepia(0.22)",
              }}
              aria-hidden="true"
            />
          </div>
        </div>
      </section>

      {/* Novedad: recitales */}
      {audioCount > 0 ? (
        <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 -mt-6 md:-mt-10 mb-4">
          <Link
            to="/audios"
            data-testid="home-recital-banner"
            className="group flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 p-6 md:p-8 border-2 transition-all hover:shadow-lg"
            style={{
              borderColor: "var(--rincon-primary)",
              background: "var(--rincon-surface)",
            }}
          >
            <div
              className="font-display text-4xl md:text-5xl shrink-0"
              style={{ color: "var(--rincon-primary)" }}
              aria-hidden="true"
            >
              ❦
            </div>
            <div className="flex-1">
              <div
                className="text-[10px] uppercase tracking-[0.3em] font-semibold mb-1"
                style={{ color: "var(--rincon-secondary)" }}
              >
                Novedad
              </div>
              <div
                className="font-display text-xl md:text-2xl tracking-tight"
                style={{ color: "var(--rincon-text)" }}
              >
                <span className="font-bold" style={{ color: "var(--rincon-primary)" }}>
                  {audioCount}
                </span>{" "}
                {audioCount === 1 ? "poema ahora con voz" : "poemas ahora con voz"}
                <span
                  className="font-body italic text-base ml-1"
                  style={{ color: "var(--rincon-text-muted)" }}
                >
                  · recitados por el autor, con música
                </span>
              </div>
            </div>
            <div
              className="font-display text-lg ink-link shrink-0 transition-colors group-hover:text-[var(--rincon-secondary)]"
              style={{ color: "var(--rincon-primary)" }}
            >
              → Entrar en El Poemario
            </div>
          </Link>
        </section>
      ) : null}

      {/* Counters — compact, centered, half-width on desktop */}
      <section
        className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-10 border-t border-b"
        style={{ borderColor: "var(--rincon-border)" }}
        data-testid="counters-section"
      >
        {trending && trending.text ? (
          <Link
            to={`/texto/${trending.text.id}`}
            data-testid="trending-banner"
            className="block mb-8 group"
          >
            <div
              className="flex items-center gap-3 text-xs uppercase tracking-[0.3em]"
              style={{ color: "var(--rincon-secondary)" }}
            >
              <span
                className="inline-block w-1.5 h-1.5 rounded-full animate-pulse"
                style={{ background: "var(--rincon-primary)" }}
              />
              Leyéndose ahora
            </div>
            <div
              className="mt-2 font-display text-lg md:text-xl italic tracking-tight group-hover:text-[var(--rincon-primary)] transition-colors"
              style={{ color: "var(--rincon-text)" }}
            >
              «{trending.text.title}»
              <span
                className="ml-3 text-xs not-italic"
                style={{ color: "var(--rincon-text-muted)" }}
              >
                · {trending.count} {trending.count === 1 ? "lectura" : "lecturas"} esta semana
              </span>
            </div>
          </Link>
        ) : null}

        <div className="md:max-w-3xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
            <div
              className="text-xs uppercase tracking-[0.3em] font-semibold"
              style={{ color: "var(--rincon-primary)" }}
            >
              El cuaderno en cifras
            </div>
            <div
              className="font-display text-base"
              style={{ color: "var(--rincon-text)" }}
            >
              <span
                className="font-bold"
                style={{ color: "var(--rincon-primary)" }}
                data-testid="counter-total"
              >
                {totalAll}
              </span>{" "}
              <span style={{ color: "var(--rincon-text-muted)" }}>
                {totalAll === 1 ? "texto" : "textos"}
              </span>
            </div>
          </div>

          <div
            className="grid grid-cols-4 gap-0 border-l-2 border-t-2"
            style={{ borderColor: "var(--rincon-primary)" }}
          >
            {TYPE_CARDS.map((c) => (
              <Link
                key={c.key}
                to={c.to}
                data-testid={`counter-${c.key.toLowerCase()}`}
                className="group px-3 py-5 border-r-2 border-b-2 transition-colors hover:bg-[var(--rincon-surface)] text-center"
                style={{ borderColor: "var(--rincon-primary)" }}
              >
                <div
                  className="font-display text-2xl md:text-3xl font-bold transition-colors group-hover:text-[var(--rincon-secondary)]"
                  style={{ color: "var(--rincon-primary)" }}
                >
                  {countFor(c.key)}
                </div>
                <div
                  className="text-[10px] uppercase tracking-[0.2em] mt-1 font-semibold"
                  style={{ color: "var(--rincon-text-muted)" }}
                >
                  {c.label}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Sections by type */}
      <section
        className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 pt-16 pb-32"
        data-testid="type-sections"
      >
        <div
          className="flex items-end justify-between flex-wrap gap-4 mb-12 pb-6 border-b"
          style={{ borderColor: "var(--rincon-border)" }}
        >
          <div>
            <div
              className="text-xs uppercase tracking-[0.3em] mb-2"
              style={{ color: "var(--rincon-secondary)" }}
            >
              Cuaderno abierto
            </div>
            <h2
              className="font-display text-3xl md:text-4xl tracking-tight"
              style={{ color: "var(--rincon-text)" }}
            >
              Explora por tipo de texto
            </h2>
          </div>
          <button
            type="button"
            onClick={openRandom}
            disabled={randomLoading || totalAll === 0}
            data-testid="home-random-button"
            className="inline-flex items-center gap-2 font-display text-lg ink-link disabled:opacity-40"
            style={{ color: "var(--rincon-primary)" }}
          >
            <Shuffle className="w-4 h-4" />
            {randomLoading ? "Buscando..." : "Leer al azar"}
          </button>
        </div>

        {loading ? (
          <div
            className="py-24 text-center font-display italic text-2xl"
            style={{ color: "var(--rincon-text-muted)" }}
            data-testid="home-loading"
          >
            Cargando...
          </div>
        ) : totalAll === 0 ? (
          <div
            className="py-24 text-center font-display italic text-2xl"
            style={{ color: "var(--rincon-text-muted)" }}
          >
            El cuaderno está aún en blanco. Pronto habrá palabras.
          </div>
        ) : (
          <div className="space-y-20">
            {SECTIONS.map((s) => {
              const items = bySub[s.sub] || [];
              if (items.length === 0) return null;
              return (
                <div key={s.sub} data-testid={`section-${s.sub.toLowerCase()}`}>
                  <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                    <div>
                      <div
                        className="text-xs uppercase tracking-[0.3em] mb-1"
                        style={{ color: "var(--rincon-secondary)" }}
                      >
                        {s.overline}
                      </div>
                      <h3
                        className="font-display text-2xl md:text-3xl tracking-tight"
                        style={{ color: "var(--rincon-text)" }}
                      >
                        {s.label}
                      </h3>
                    </div>
                    <Link
                      to={s.to}
                      className="font-display text-base ink-link"
                      style={{ color: "var(--rincon-primary)" }}
                      data-testid={`section-more-${s.sub.toLowerCase()}`}
                    >
                      Ver todo →
                    </Link>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {items.slice(0, 6).map((t, i) => (
                      <TextCardGrid key={t.id} text={t} index={i} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
